// Itinerary, per-guest arrangements and the shared Flight + ride line. Pure and dependency-free: the client app builds this file too.
// The database (migration 294) decides everything; this file only words it, so coordinator and dispatcher read the same sentence.
import { clockLabel } from './foundation';

export const ITEM_TYPES = [
    { key: 'session', label: 'Session' }, { key: 'meal', label: 'Meal' }, { key: 'activity', label: 'Activity' },
    { key: 'free_time', label: 'Free time' }, { key: 'transfer', label: 'Transfer' }, { key: 'other', label: 'Other' },
];
export const itemTypeLabel = (key) => ITEM_TYPES.find((t) => t.key === key)?.label || key;
export const AUDIENCE_TAGS = [{ key: 'local', label: 'Local' }, { key: 'driving', label: 'Driving' }, { key: 'staff', label: 'Staff / employee' }, { key: 'rep', label: 'Rep' }];
export const TRANSFER_RIDERS = [
    { key: 'audience', label: 'The people chosen under Who' }, { key: 'all', label: 'Everyone (including locals)' }, { key: 'arrivals', label: 'Arriving flyers' },
    { key: 'departures', label: 'Departing flyers' }, { key: 'locals', label: 'Locals only' }, { key: 'custom', label: 'A set number' },
];
export const TRANSFER_TIMINGS = [{ key: 'fixed', label: 'At a set time' }, { key: 'arrivals', label: 'Tied to arriving flights' }, { key: 'departures', label: 'Tied to departing flights' }];
export const OTHER_ARRANGEMENTS = [
    { key: 'self', label: 'Self-arranged' }, { key: 'rental', label: 'Rental car' }, { key: 'client', label: 'Client-arranged' }, { key: 'local', label: 'Local (no transport needed)' },
];
export const arrangementLabel = (key) => OTHER_ARRANGEMENTS.find((a) => a.key === key)?.label || '';

export function audienceLabel(item) {
    if (item.audience_kind === 'tag') return `Guests tagged ${AUDIENCE_TAGS.find((t) => t.key === item.audience_tag)?.label || item.audience_tag}`;
    if (item.audience_kind === 'group') return item.group_name || 'A group that was removed';
    return 'Everyone';
}
export const dayDate = (startDate, dayNumber) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate || '')) return '';
    const d = new Date(`${startDate}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + dayNumber - 1);
    return d.toISOString().slice(0, 10);
};
export function dayLabel(startDate, dayNumber) {
    const date = dayDate(startDate, dayNumber);
    if (!date) return `Day ${dayNumber}`;
    return `Day ${dayNumber} · ${new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })}`;
}
export function itemWhen(item) {
    if (item.item_type === 'transfer' && item.transfer_timing === 'arrivals') return 'With arriving flights';
    if (item.item_type === 'transfer' && item.transfer_timing === 'departures') return 'With departing flights';
    if (!item.start_time) return 'Time to be set';
    return item.end_time ? `${clockLabel(item.start_time)} – ${clockLabel(item.end_time)}` : clockLabel(item.start_time);
}

const minutes = (hhmm) => { const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || ''); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
const sameAudience = (a, b) => a.audience_kind === 'everyone' || b.audience_kind === 'everyone'
    || (a.audience_kind === b.audience_kind && (a.audience_kind === 'tag' ? a.audience_tag === b.audience_tag : a.audience_group_id === b.audience_group_id));

/** Hints only, never blocks: overlapping items for the same people, and a Transfer that arrives after the next item starts. Returns { itemId: [message] }. */
export function itineraryConflicts(items = []) {
    const hints = {};
    const add = (id, text) => { (hints[id] = hints[id] || []).push(text); };
    const byDay = new Map();
    items.forEach((i) => { if (minutes(i.start_time) != null) byDay.set(i.day_number, [...(byDay.get(i.day_number) || []), i]); });
    byDay.forEach((list) => {
        const sorted = [...list].sort((a, b) => minutes(a.start_time) - minutes(b.start_time));
        sorted.forEach((a, n) => {
            const aEnd = minutes(a.end_time) ?? minutes(a.start_time);
            sorted.slice(n + 1).forEach((b) => {
                if (!sameAudience(a, b)) return;
                if (minutes(b.start_time) < aEnd) {
                    add(a.id, `Overlaps “${b.title}” for the same people.`); add(b.id, `Overlaps “${a.title}” for the same people.`);
                    if (a.item_type === 'transfer' && a.end_time) add(a.id, `Arrives after “${b.title}” starts.`);
                }
            });
        });
    });
    return hints;
}

/** Which ticks a guest shows. "n/a" means it does not apply to them. */
export function arrangementTicks(a = {}) {
    const tick = (ok, needed) => (needed === false ? 'n/a' : ok ? 'yes' : 'no');
    return { flight: tick(a.flight, a.flight_needed), hotel: tick(a.hotel, a.hotel_needed), pickup: tick(a.pickup, a.pickup_needed) };
}
const MARK = { yes: '✓', no: '✗', 'n/a': '–' };
export const arrangementLine = (a) => { const t = arrangementTicks(a); return `Flight ${MARK[t.flight]} · Hotel ${MARK[t.hotel]} · Pickup ${MARK[t.pickup]}`; };

const RIDE_WORDS = { requested: 'Requested', confirmed: 'Confirmed', driver_assigned: 'Driver assigned', en_route: 'En route', picked_up: 'Picked up', completed: 'Completed', cancelled: 'Cancelled', no_show: 'No show' };
export const rideStatusLabel = (status) => RIDE_WORDS[status] || 'Requested';

export function flightStatusLabel(flight = {}) {
    const late = flight.delay_minutes != null ? Math.abs(flight.delay_minutes) : null;
    let words;
    switch (flight.state) {
    case 'cancelled': words = 'Cancelled'; break;
    case 'diverted': words = 'Diverted'; break;
    case 'landed': words = 'Landed'; break;
    case 'departed': words = 'Departed'; break;
    case 'delayed': words = late ? `Delayed ${late}m` : 'Delayed'; break;
    case 'early': words = late ? `Early ${late}m` : 'Early'; break;
    case 'on_time': words = 'On time'; break;
    default: words = 'Not tracked';
    }
    const where = [flight.terminal ? `Terminal ${flight.terminal}` : '', flight.gate ? `Gate ${flight.gate}` : ''].filter(Boolean).join(' ');
    return where ? `${words} · ${where}` : words;
}
const when = (value, zone) => (value ? new Date(value).toLocaleString('en-US', { timeZone: zone || undefined, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

/** The one line, word for word the same on the event page and the dispatcher board. */
export function flightRideText(line, zone) {
    const f = line.flight || {};
    const head = `${line.direction === 'departure' ? 'Departing' : 'Arriving'} ${f.flight_number || 'flight'}: ${flightStatusLabel(f)}`;
    const rides = line.rides || [];
    if (!rides.length) return `${head} · No ride yet`;
    return `${head} · ${rides.map((r) => [rideStatusLabel(r.status), r.pickup_at ? `Pickup ${when(r.pickup_at, zone)}` : '', r.driver ? `Driver ${r.driver}` : '', r.vehicle || ''].filter(Boolean).join(', ')).join(' | ')}`;
}
